// src/pages/account/admin/ContactMessages.js
import React, { useEffect, useState } from "react";
import { db } from "../../../shared/libs/firebaseConfig";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { useAuth } from "../../../shared/auth/AuthProvider";

export default function ContactMessages() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // เก็บค่า input ของ tags แยกตาม message id
  const [tagInputs, setTagInputs] = useState({});

  useEffect(() => {
    document.title = "Contact Messages - Admin - Encraft";
  }, []);

  // โหลดข้อมูลแบบ realtime
  useEffect(() => {
    setLoading(true);
    setError("");

    const q = query(
      collection(db, "contactMessages"),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map((d) => {
          const data = d.data();
          const tagsArray =
            data.tags && Array.isArray(data.tags) ? data.tags : [];
          return {
            id: d.id,
            ...data,
            tags: tagsArray,
          };
        });

        setMessages(items);

        // sync tagInputs เมื่อโหลดครั้งแรก/มีการเปลี่ยนแปลง
        setTagInputs((prev) => {
          const next = { ...prev };
          items.forEach((m) => {
            if (!next[m.id]) {
              next[m.id] = m.tags.join(", ");
            }
          });
          return next;
        });

        setLoading(false);
      },
      (err) => {
        console.error("Error loading contact messages:", err);
        setError("ไม่สามารถโหลดข้อมูลข้อความติดต่อได้");
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const handleChangeStatus = async (id, newStatus) => {
    try {
      await updateDoc(doc(db, "contactMessages", id), {
        status: newStatus,
      });
    } catch (err) {
      console.error("Error updating status:", err);
      alert("ไม่สามารถเปลี่ยนสถานะได้");
    }
  };

  const handleTagInputChange = (id, value) => {
    setTagInputs((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSaveTags = async (id) => {
    const tagsString = tagInputs[id] || "";
    const tags = tagsString
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    try {
      await updateDoc(doc(db, "contactMessages", id), {
        tags,
      });
    } catch (err) {
      console.error("Error updating tags:", err);
      alert("ไม่สามารถบันทึกแท็กได้");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("ต้องการลบข้อความนี้จริงหรือไม่?")) return;
    try {
      await deleteDoc(doc(db, "contactMessages", id));
    } catch (err) {
      console.error("Error deleting message:", err);
      alert("ไม่สามารถลบข้อความได้");
    }
  };

  const formatDate = (ts) => {
    if (!ts) return "-";
    const d = ts.toDate ? ts.toDate() : ts;
    return d.toLocaleString("th-TH", {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  const filteredMessages = messages.filter((m) => {
    if (statusFilter !== "all" && m.status !== statusFilter) return false;
    if (!search) return true;
    const text =
      `${m.name || ""} ${m.email || ""} ${m.subject || ""} ${
        m.message || ""
      }`.toLowerCase();
    return text.includes(search.toLowerCase());
  });

  return (
    <div className="py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="h4 mb-0">Contact Messages</h2>
          {/* <div className="text-muted small">
            ดูและจัดการข้อความที่ส่งจากหน้า &quot;ติดต่อเรา&quot;
          </div> */}
          {user && (
          <span className="badge bg-light text-muted">
            Admin: {user.email}
          </span>
        )}
        </div>
        
      </div>

      {/* แถบ filter */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-center">
            <div className="col-md-4">
              <label className="form-label small mb-1">ค้นหา</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="ค้นหาจากชื่อ / อีเมล / หัวข้อ / ข้อความ"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">สถานะ</label>
              <select
                className="form-select form-select-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">ทั้งหมด</option>
                <option value="new">ใหม่ (new)</option>
                <option value="read">อ่านแล้ว (read)</option>
                <option value="archived">เก็บถาวร (archived)</option>
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <div className="small text-muted">
                ทั้งหมด {filteredMessages.length} รายการ
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && <div>กำลังโหลดข้อความ...</div>}
      {error && <div className="text-danger small mb-3">{error}</div>}

      {!loading && filteredMessages.length === 0 && (
        <div className="text-muted">ยังไม่มีข้อความติดต่อ</div>
      )}

      <div className="d-flex flex-column gap-3">
        {!loading &&
          filteredMessages.map((m) => (
            <div
              key={m.id}
              className={
                "card" + (m.status === "new" ? " border-primary" : " border-light")
              }
            >
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
  {/* ซ้าย: subject + badge + meta */}
  <div>
    <div className="d-flex align-items-center gap-2 mb-1">
      <strong>{m.subject || "(ไม่มีหัวข้อ)"}</strong>

      {m.status === "new" && (
        <span className="badge bg-primary">NEW</span>
      )}
      {m.status === "read" && (
        <span className="badge bg-secondary">READ</span>
      )}
      {m.status === "archived" && (
        <span className="badge bg-light text-muted">ARCHIVED</span>
      )}
    </div>

    <div className="small text-muted">
      จาก: {m.name || "-"} &lt;{m.email || "-"}&gt;
      <br/>วันที่: {formatDate(m.createdAt)} • ที่มา: {m.source || "web-contact"}
    </div>
  </div>

  {/* ขวา: ปุ่ม [ใหม่][อ่านแล้ว][เก็บถาวร][ลบข้อความ] */}
  <div className="d-flex align-items-center gap-2">
    <div className="btn-group btn-group-sm">
      {m.status !== "new" && (
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => handleChangeStatus(m.id, "new")}
        >
          ใหม่
        </button>
      )}
      {m.status !== "read" && (
        <button
          type="button"
          className="btn btn-outline-success"
          onClick={() => handleChangeStatus(m.id, "read")}
        >
          อ่านแล้ว
        </button>
      )}
      {m.status !== "archived" && (
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => handleChangeStatus(m.id, "archived")}
        >
          เก็บถาวร
        </button>
      )}
    </div>

    <button
      type="button"
      className="btn btn-sm btn-link text-danger p-0"
      onClick={() => handleDelete(m.id)}
    >
      ลบข้อความ
    </button>
  </div>
</div>


                <hr className="my-2" />

                <p className="mb-3" style={{ whiteSpace: "pre-wrap" }}>
                  {m.message}
                </p>

                <div className="small">
                  <label className="form-label small mb-1">Tags</label>
                  <div className="d-flex gap-2">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="เช่น bug, feature-request, collab"
                      value={tagInputs[m.id] || ""}
                      onChange={(e) =>
                        handleTagInputChange(m.id, e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleSaveTags(m.id)}
                    >
                      บันทึก
                    </button>
                  </div>
                  {m.tags && m.tags.length > 0 && (
                    <div className="mt-2 d-flex flex-wrap gap-1">
                      {m.tags.map((t) => (
                        <span key={t} className="badge bg-light text-muted">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
